import { Event } from 'schema-dts';
import { JsonLd } from 'react-schemaorg';
import { LocalDate, LocalTime } from 'js-joda';
import { consumer } from '@tock/coretypes';
import { flatMap, isEmpty } from 'es-toolkit/compat';
import { getExperienceImages } from 'head/experienceImages';
import { getSameAs } from 'utils/getSameAs';
import { matchPath, useLocation } from 'react-router-dom';
import { minDate, resolveSchedule } from '@tock/time';

interface EventDetailJsonLdProps {
  offering: consumer.Offering;
  business: consumer.BusinessConfig;
}

export const EventDetailJsonLd: React.FC<EventDetailJsonLdProps> = ({ offering, business }) => {
  const location = useLocation();
  const eventDetails = offering?.eventDetails;
  const schedules = eventDetails?.schedule ?? [];
  const date = eventDetails?.date;
  const time = eventDetails?.startTime;
  const experienceDetailStartTime = !isEmpty(schedules)
    ? minDate(flatMap(schedules, resolveSchedule))
    : date && time
      ? LocalDate.parse(date).atTime(LocalTime.parse(time))
      : date
        ? LocalDate.parse(date)
        : undefined;

  const eventImages = getExperienceImages(offering);

  const eventPageMatch = matchPath(location.pathname, {
    path: [
      '/:domainName/event/:experienceId/:experienceSlug?',
      '/:domainName/event/private/:ticketTypeAccessToken',
    ],
    exact: true,
  });

  const showEventMicrodata =
    eventPageMatch && offering && !business?.unlisted && experienceDetailStartTime;

  if (!showEventMicrodata) {
    return null;
  }

  return (
    <JsonLd<Event>
      item={{
        '@context': 'https://schema.org',
        '@type': 'Event',
        '@id': `https://www.exploretock.com${location.pathname}`,
        name: offering.name,
        url: `https://www.exploretock.com${location.pathname}`,
        organizer: {
          '@type': 'Organization',
          '@id': `https://www.exploretock.com${business.domainName}`,
          name: business.name,
          sameAs: getSameAs(business) ?? [],
        },
        startDate: experienceDetailStartTime?.toString(),
        image: eventImages,
        description: offering.description,
        location: {
          '@type': 'Place',
          name: offering.eventDetails?.location?.name ?? '',
          address: {
            '@type': 'PostalAddress',
            streetAddress: offering.eventDetails?.location?.address ?? undefined,
            addressLocality: offering.eventDetails?.location?.city ?? undefined,
            addressRegion: offering.eventDetails?.location?.state ?? undefined,
            postalCode: offering.eventDetails?.location?.zipCode ?? undefined,
            addressCountry: offering.eventDetails?.location?.country ?? undefined,
          },
        },
        offers: {
          '@type': 'Offer',
          url: `https://www.exploretock.com${location.pathname}?utm_source=search`,
          availability:
            offering.state === 'AVAILABLE'
              ? 'https://schema.org/InStock'
              : 'https://schema.org/SoldOut',
          price: offering.price?.pricePerPerson?.minCents
            ? offering.price.pricePerPerson.minCents / 100
            : undefined,
          priceCurrency: business.currencyCode,
        },
      }}
    />
  );
};
